import { RangeType, Tool } from '@app/shared/utils/enums';
import { Injectable } from '@angular/core';
import { FetchProductDataFilters } from '@app/core/models/fetch-product-data-filters';
import {
  ProductCatalogFilterBreedSizevalues,
  ProductCatalogFilterValues,
} from '@app/shared/components/product-catalog-filters/product-catalog-filters';
import { Store } from '@ngrx/store';
import { AppState } from '..';
import {
  addPricesToUpdate,
  getAllProducts,
  resetProductsState,
  setProductFilters,
  updatePrices,
  resetProductsPricesState,
  updateSelectedCategories,
  setSelectedProduct,
  resetSelectedProduct,
  setSelectedDryProductPackId,
  setSelectedWetProductPackId,
  setSelectedTreatsProductPackId,
  removePackFromPriceListToUpdate,
  removePacksFromPriceListToUpdate,
  setPriceMargin,
  addSelectedCategory,
  removeSelectedCategory,
} from './products.actions';
import {
  selectFilteredProducts,
  selectGetAllProductsFail,
  selectSelectedCategories,
  selectProductsLoading,
  selectFilteredProductsLength,
  selectPriceListToUpdate,
  selectShowPriceListActions,
  selectPriceListUpdateLoading,
  selectFiltersValues,
  selectHasWetProduct,
  selectHasDryProduct,
  selectMufProductsBySpecie,
  selectSelectedPackIds,
  selectSelectedProducts,
  selectSelectedDryProduct,
  selectSelectedWetProduct,
  selectSelectedDryPack,
  selectSelectedWetPack,
  selectFilteredCAProductsFlatten,
  selectProductBookCategories,
  selectSelectedTreatsPack,
  selectSelectedTreatsProduct,
  selectHasTreatsProduct,
  selectSpecificSpecieCode,
  selectSpecificLifestageType,
} from './products.selectors';
import { ProductPriceEdit, Product } from '@app/core/models';
import { TerritoryType } from '@app/shared/utils/enums/territoryType';

@Injectable()
export class ProductsFacade {
  selectedProducts$ = this.store$.select(selectSelectedProducts);
  selectedPackIds$ = this.store$.select(selectSelectedPackIds);
  hasDryProduct$ = this.store$.select(selectHasDryProduct);
  hasWetProduct$ = this.store$.select(selectHasWetProduct);
  hasTreatsProduct$ = this.store$.select(selectHasTreatsProduct);
  selectedDryProduct$ = this.store$.select(selectSelectedDryProduct);
  selectedWetProduct$ = this.store$.select(selectSelectedWetProduct);
  selectedTreatsProduct$ = this.store$.select(selectSelectedTreatsProduct);
  selectedDryPack$ = this.store$.select(selectSelectedDryPack);
  selectedWetPack$ = this.store$.select(selectSelectedWetPack);
  selectedTreatsPack$ = this.store$.select(selectSelectedTreatsPack);

  priceListToUpdate$ = this.store$.select(selectPriceListToUpdate);
  priceListUpdateLoading$ = this.store$.select(selectPriceListUpdateLoading);
  showPriceListActions$ = this.store$.select(selectShowPriceListActions);

  productsLoading$ = this.store$.select(selectProductsLoading);
  getAllProductsFail$ = this.store$.select(selectGetAllProductsFail);
  filteredProducts$ = this.store$.select(selectFilteredProducts);
  filteredCAProductsFlatten$ = this.store$.select(selectFilteredCAProductsFlatten);
  filteredProductsLength$ = this.store$.select(selectFilteredProductsLength);
  filtersValue$ = this.store$.select(selectFiltersValues);
  selectedCategories$ = this.store$.select(selectSelectedCategories);
  mufProductsBySpecie$ = this.store$.select(selectMufProductsBySpecie);
  specificSpecieCode$ = this.store$.select(selectSpecificSpecieCode);
  specificLifestageType$ = this.store$.select(selectSpecificLifestageType);

  selectProductBookCategories$ = (isSolProductsInVetEnable: boolean) => {
    return this.store$.select(selectProductBookCategories(isSolProductsInVetEnable));
  };

  constructor(private readonly store$: Store<AppState>) {}

  /** Price Actions */
  addPricesToUpdate(prices: ProductPriceEdit[]): void {
    this.store$.dispatch(addPricesToUpdate({ prices }));
  }

  removePackFromPriceListToUpdate(productId: string, packEan: string): void {
    this.store$.dispatch(removePackFromPriceListToUpdate({ productId, packEan }));
  }

  removePacksFromPriceListToUpdate(products: { productId: string; packEan: string }[]): void {
    this.store$.dispatch(removePacksFromPriceListToUpdate({ products }));
  }

  updatePrices(
    withTracking = true,
    trackingShouldIncludeProductsInformation = true,
    updateAllPrices = true,
    productsToUpdate: ProductPriceEdit[] = [],
  ): void {
    this.store$.dispatch(updatePrices({ withTracking, trackingShouldIncludeProductsInformation, updateAllPrices, productsToUpdate }));
  }

  resetProductsPricesState(): void {
    this.store$.dispatch(resetProductsPricesState());
  }

  setPriceMargin(priceMargin: number): void {
    this.store$.dispatch(setPriceMargin({ priceMargin }));
  }

  /** All Product Actions */
  getAllProducts(filterParams?: FetchProductDataFilters, tool?: Tool): void {
    this.store$.dispatch(getAllProducts({ filterParams, tool }));
  }

  /** Products Filters Actions */
  setProductFilters(filtersValues: ProductCatalogFilterValues & ProductCatalogFilterBreedSizevalues): void {
    this.store$.dispatch(setProductFilters({ filtersValues }));
  }

  updateSelectedCategories(selectedCategories: (TerritoryType | RangeType)[] | null): void {
    this.store$.dispatch(updateSelectedCategories({ selectedCategories }));
  }

  addSelectedCategory(category: TerritoryType | RangeType): void {
    this.store$.dispatch(addSelectedCategory({ category }));
  }

  removeSelectedCategory(category: TerritoryType | RangeType): void {
    this.store$.dispatch(removeSelectedCategory({ category }));
  }

  /** Products Catalog Actions */
  setSelectedDryProductPackId(packId: string): void {
    this.store$.dispatch(setSelectedDryProductPackId({ packId }));
  }

  setSelectedWetProductPackId(packId: string): void {
    this.store$.dispatch(setSelectedWetProductPackId({ packId }));
  }

  setSelectedTreatsProductPackId(packId: string): void {
    this.store$.dispatch(setSelectedTreatsProductPackId({ packId }));
  }

  setSelectedProduct({ product, packId }: { product: Product; packId?: string }, allowMultipleSelection = false): void {
    this.store$.dispatch(setSelectedProduct({ selectedProduct: { product, packId }, allowMultipleSelection }));
  }

  resetSelectedProduct(productId: string): void {
    this.store$.dispatch(resetSelectedProduct({ productId }));
  }

  resetProductsState(): void {
    this.store$.dispatch(resetProductsState());
  }
}
